// mc-deposit.ts — what a manager's-cheque deposit IS, as a fact derived from
// the figures, never a claim typed beside them.
//
// WHY THIS EXISTS (30 Sept 2026). The cheque screen carried one "Deposit %"
// box that did two jobs: it seeded the AED box, and it stayed on screen as a
// description of the deposit — while the operator typed the amount the
// customer actually paid. The two parted the moment a figure was typed, and
// the box went on saying "2" beside a deposit that was 44% of the cheque. The
// same "2" was then SENT and STORED on admin.deals.mc_deposit_pct and in the
// :book posting's metadata: three of the four cheques booked that morning
// carry a 2% that was never true of them. The operator's words: "2% is not
// always exactly how we do it".
//
// So the share is computed here from the two figures that are true — what the
// customer owes (face + commission) and what they put down in AED — and every
// reader takes it from here. The prefill box remains a prefill.

/** Half a fils: the tolerance the screen already uses to decide whether a
 *  typed AED figure differs from the prefill. Figures inside it are equal. */
const FILS = 0.005;

export interface DepositFacts {
  /** Both figures are present, so the rest is meaningful. */
  known: boolean;
  /** The deposit as a share of face + commission, in percent (44.38, not 0.4438). */
  sharePct: number;
  /** What stage 3 still has to collect, in AED, never below zero. */
  leaves: number;
  /** The deposit covers face + commission (to the fils). */
  paidInFull: boolean;
  /** How much MORE than face + commission was put down; 0 when not over. */
  over: number;
}

export function depositFacts(owedAed: number, depositAed: number): DepositFacts {
  const known = owedAed > 0 && depositAed > 0;
  const leaves = known ? owedAed - depositAed : owedAed;
  return {
    known,
    sharePct: known ? (depositAed / owedAed) * 100 : 0,
    leaves: Math.max(0, leaves),
    paidInFull: known && leaves <= FILS,
    over: known && leaves < -FILS ? -leaves : 0,
  };
}

/** A percentage for a sentence: up to two decimals, trailing zeros dropped —
 *  "2", "0.55", "44.38", "100". Never "2.00" beside a box that says 2. */
export function pctWords(pct: number): string {
  if (!Number.isFinite(pct)) return '0';
  return String(Number(pct.toFixed(2)));
}
